/**
 * The call's media transport while it lives: direct (P2P) or the bridge
 * (JVB), each move between them, and the two guards that keep a direct call
 * from freezing (p2pPolicy.ts decides whether a room may run direct at all).
 *
 * lib-jitsi-meet moves a call on its own: to the bridge when a third person
 * joins, back to direct once it is two again. What it does not do, and this
 * does, per conference:
 *
 *   - Hidden peers. A hidden participant (the manager observer, the Jibri
 *     recorder) counts as the one other peer in lib-jitsi-meet, so a person
 *     left alone with an observer would open a direct session to them and
 *     skip the bridge's receive-only rule. The conference's
 *     `_shouldBeInP2PMode`, which decides both starting and accepting a
 *     direct session, also requires that nobody hidden is in the room.
 *   - A direct connection that drops. lib-jitsi-meet leaves the direct
 *     session only when its ICE reaches `failed`, which can take about 30 s
 *     of frozen video after a network change, while the bridge connection
 *     is still up. After P2P_INTERRUPTED_FALLBACK_MS of interruption the call
 *     moves to the bridge.
 *   - A move to direct that brings no video. If the other side's camera is
 *     on but none of its packets arrive over the direct connection within
 *     TRANSPORT_SETTLE_TIMEOUT_MS (WebRTC keeps a remote track muted until
 *     its first packets), the call moves back to the bridge. Whether a frame
 *     was drawn is not the test: a room with nothing showing that video, or
 *     an app in the background, draws none. A device that receives no video
 *     on purpose (lastN 0: the native app in the background or audio only,
 *     which also asks the other side to stop sending it direct) owes none.
 *
 * After either fallback the conference stays on the bridge until it ends:
 * the same network would fail the same way. A reload is a new conference.
 *
 * Each move is reported once it settles (the first frame over the new
 * transport, or the timeout), at most MAX_REPORTED_SWITCHES per conference;
 * the count keeps going for the quality samples.
 *
 * Raw lib-jitsi-meet event names are used on purpose, as in
 * interpreter-queue/middleware.ts: importing the SDK's enums would drag it
 * into every sandboxed harness that loads this file.
 */
export const P2P_INTERRUPTED_FALLBACK_MS = 4000;
export const TRANSPORT_SETTLE_TIMEOUT_MS = 6000;
export const MAX_REPORTED_SWITCHES = 8;
/** JitsiConferenceEvents.CONNECTION_INTERRUPTED / CONNECTION_RESTORED / P2P_STATUS. */
export const LJM_CONNECTION_INTERRUPTED = 'conference.connectionInterrupted';
export const LJM_CONNECTION_RESTORED = 'conference.connectionRestored';
/**
 * Emitted with (conference, isP2P) whenever the call moves, before the
 * remote tracks are swapped. Only the web app turns it into redux
 * (P2P_STATUS_CHANGED), so both platforms listen to the conference.
 */
export const LJM_P2P_STATUS = 'conference.p2pStatus';
function hasHiddenPeer(conference) {
    try {
        return (conference.getParticipants?.() || []).some(participant => participant?.isHidden?.() === true);
    }
    catch {
        return true;
    }
}
/**
 * Wraps the conference's own P2P rule with ours: no hidden peer, no fallback
 * taken. Returns false when this lib-jitsi-meet has no such rule to wrap; the
 * caller then keeps the conference off direct media (fail closed).
 *
 * @param {IP2PConference} conference - The conference, before it joins.
 * @param {Function} fellBack - Whether this conference already fell back to the bridge.
 * @returns {boolean}
 */
export function guardConferenceP2P(conference, fellBack) {
    const original = conference._shouldBeInP2PMode;
    if (typeof original !== 'function') {
        return false;
    }
    conference._shouldBeInP2PMode = function shouldBeInP2PMode() {
        return original.call(this) === true && !fellBack() && !hasHiddenPeer(conference);
    };
    return true;
}
export class TransportMonitor {
    constructor(conference, options) {
        this.transport = 'jvb';
        this.switches = 0;
        this.reported = 0;
        this.disposed = false;
        this.conference = conference;
        this.options = {
            clearTimer: options.clearTimer || (timer => clearTimeout(timer)),
            now: options.now || (() => Date.now()),
            remoteVideo: options.remoteVideo,
            report: options.report,
            setTimer: options.setTimer || ((callback, ms) => setTimeout(callback, ms))
        };
        this.joinedAt = this.options.now();
        this.onInterrupted = () => this.interrupted();
        this.onRestored = () => this.restored();
        this.onP2PStatus = (_conference, p2p) => this.statusChanged(p2p === true);
        try {
            conference.on?.(LJM_P2P_STATUS, this.onP2PStatus);
            conference.on?.(LJM_CONNECTION_INTERRUPTED, this.onInterrupted);
            conference.on?.(LJM_CONNECTION_RESTORED, this.onRestored);
        }
        catch { /* the guards are then off; lib-jitsi-meet's own handling stands */ }
    }
    /** The conference fell back to the bridge and stays there. */
    fellBack() {
        return this.fallbackTaken;
    }
    /** Moves so far, for the quality samples. */
    switchCount() {
        return this.switches;
    }
    currentTransport() {
        return this.transport;
    }
    /** The call is now direct (true) or on the bridge (LJM_P2P_STATUS). */
    statusChanged(p2p) {
        const to = p2p ? 'p2p' : 'jvb';
        if (this.disposed || to === this.transport) {
            return;
        }
        this.transport = to;
        if (!p2p) {
            this.clearInterrupt();
        }
        this.finish('superseded');
        this.switches += 1;
        const pending = {
            at: this.options.now(),
            fallback: this.fallbackForNextSwitch,
            index: this.switches,
            to
        };
        this.fallbackForNextSwitch = undefined;
        pending.timer = this.options.setTimer(() => this.settleTimedOut(pending), TRANSPORT_SETTLE_TIMEOUT_MS);
        this.pending = pending;
    }
    /** The first frame of one of the other side's video tracks played. */
    remoteVideoStarted(isP2P) {
        if (this.pending && (this.pending.to === 'p2p') === isP2P) {
            this.finish('frame');
        }
    }
    /** The conference is over for this device. */
    dispose() {
        if (this.disposed) {
            return;
        }
        this.finish('left');
        this.clearInterrupt();
        this.disposed = true;
        try {
            this.conference.off?.(LJM_P2P_STATUS, this.onP2PStatus);
            this.conference.off?.(LJM_CONNECTION_INTERRUPTED, this.onInterrupted);
            this.conference.off?.(LJM_CONNECTION_RESTORED, this.onRestored);
        }
        catch { /* nothing to undo */ }
    }
    /** False while this device asks for no video (lastN 0), so none is due. */
    receivesVideo() {
        try {
            return this.conference.getLastN?.() !== 0;
        }
        catch {
            return true;
        }
    }
    isDirect() {
        try {
            return this.conference.isP2PActive?.() === true;
        }
        catch {
            return false;
        }
    }
    interrupted() {
        if (this.disposed || this.interruptTimer || !this.isDirect()) {
            return;
        }
        this.interruptTimer = this.options.setTimer(() => {
            this.interruptTimer = undefined;
            if (this.isDirect()) {
                this.fallBack('p2p_interrupted');
            }
        }, P2P_INTERRUPTED_FALLBACK_MS);
    }
    restored() {
        this.clearInterrupt();
    }
    clearInterrupt() {
        if (this.interruptTimer) {
            this.options.clearTimer(this.interruptTimer);
            this.interruptTimer = undefined;
        }
    }
    settleTimedOut(pending) {
        if (this.pending !== pending || this.disposed) {
            return;
        }
        pending.timer = undefined;
        let flow;
        try {
            flow = this.receivesVideo() ? this.options.remoteVideo(pending.to) : 'none';
        }
        catch {
            flow = 'unknown';
        }
        if (flow === 'none') {
            // Their camera is off: no frame was due.
            this.finish('no_video');
            return;
        }
        if (flow === 'flowing') {
            // Media arrives; nothing on this device drew it yet.
            this.finish('media');
            return;
        }
        this.finish('timeout');
        // Only a direct connection that carries nothing goes back; the
        // bridge is where it would go.
        if (flow === 'stalled' && pending.to === 'p2p' && this.isDirect()) {
            this.fallBack('p2p_no_media');
        }
    }
    fallBack(reason) {
        if (this.fallbackTaken || this.disposed) {
            return;
        }
        this.fallbackTaken = reason;
        this.fallbackForNextSwitch = reason;
        try {
            this.conference.stopP2PSession?.({
                reason: 'connectivity-error',
                reasonDescription: reason === 'p2p_interrupted' ? 'P2P interrupted' : 'P2P brought no media'
            });
        }
        catch {
            // The lib's own ICE-failed handling still moves the call later.
        }
    }
    finish(settled) {
        const pending = this.pending;
        if (!pending) {
            return;
        }
        this.pending = undefined;
        if (pending.timer) {
            this.options.clearTimer(pending.timer);
        }
        if (this.reported >= MAX_REPORTED_SWITCHES) {
            return;
        }
        this.reported += 1;
        try {
            this.options.report({
                ...(pending.fallback ? { fallback: pending.fallback } : {}),
                index: pending.index,
                settleMs: Math.max(0, this.options.now() - pending.at),
                settled,
                sinceJoinMs: Math.max(0, pending.at - this.joinedAt),
                to: pending.to
            });
        }
        catch { /* diagnostics never break the call */ }
    }
}
/**
 * The other side's camera over one transport: its unmuted camera tracks
 * there, and whether WebRTC still holds them muted for want of a first
 * packet (the MediaStreamTrack's own `muted`, which is not the camera
 * switch). Nothing drawn on screen is needed to tell.
 *
 * @param {Array} tracks - The redux tracks (features/base/tracks).
 * @param {string} transport - 'p2p' or 'jvb'.
 * @returns {RemoteVideoFlow}
 */
export function remoteVideoFlow(tracks, transport) {
    const cameras = (tracks || []).filter(track => !track.local && track.mediaType === 'video' && !track.muted
        && Boolean(track.jitsiTrack?.isP2P) === (transport === 'p2p'));
    if (!cameras.length) {
        return 'none';
    }
    const waiting = cameras.map(track => {
        try {
            return track.jitsiTrack?.getTrack?.()?.muted;
        }
        catch {
            return undefined;
        }
    });
    if (waiting.some(muted => muted === false)) {
        return 'flowing';
    }
    return waiting.every(muted => muted === true) ? 'stalled' : 'unknown';
}
/**
 * People in the room as this device counts them: visible (itself included)
 * and hidden (observers, recorders).
 *
 * @param {unknown} conference - The JitsiConference.
 * @returns {Object|undefined}
 */
export function countConferencePeople(conference) {
    const counts = conference;
    try {
        const visible = counts?.getParticipantCount?.();
        const hidden = (counts?.getParticipants?.() || []).filter(participant => participant?.isHidden?.() === true)
            .length;
        return typeof visible === 'number' ? { hidden, visible } : undefined;
    }
    catch {
        return undefined;
    }
}
let activeTransport;
/** Set by p2pTransportMiddleware.ts when a conference is created, cleared when it ends. */
export function setActiveTransport(next) {
    activeTransport = next;
}
/**
 * The live conference's transport state, optionally only if it is `conference`.
 *
 * @param {unknown} [conference] - The conference the caller is reporting on.
 * @returns {IActiveTransport|undefined}
 */
export function getActiveTransport(conference) {
    return activeTransport && (conference === undefined || activeTransport.conference === conference)
        ? activeTransport
        : undefined;
}
